import { useMemo } from 'react';

import { useDrag } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import useDialogs from '@/hooks/useDialogs';
import useIntents from '@/hooks/useIntents';
import useWebhooks from '@/hooks/useWebhooks';
import { cn } from '@/lib/utils';
import { TYPES, defaultPropsByType, iconByType } from '@/redux/dialogs/helper';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId, selectDialogId } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import styles from './ToolkitItem.module.scss';

interface ToolkitItemProps {
  id: string;
  title: string;
  type: string;
}

const ToolkitItem = ({ id, title, type }: ToolkitItemProps) => {
  const { t } = useTranslation();
  const canWrite = useSelector(
    (state: RootState) => getPermissions(state, 'brains', actions.WRITE),
    shallowEqual
  );
  const brainId = useSelector(selectBrainId);
  const { randomIntent } = useIntents(brainId);
  const { randomWebhook, actionWebhooks = [] } = useWebhooks(brainId);
  const dialogId = useSelector(selectDialogId);
  const { randomNode } = useDialogs(brainId, dialogId);
  const isWebhookDisabled = useMemo(() => {
    switch (id) {
      case 'webhook': {
        return actionWebhooks?.length === 0;
      }
      default:
        return false;
    }
  }, [actionWebhooks?.length, id]);

  const getRandomValue = (type: string) => {
    switch (type) {
      case 'intent':
        return randomIntent?.intent;
      case 'webhook':
        return randomWebhook?.webhook_id;
      case 'webview':
      case 'event':
        return randomNode?.value;
      default:
        return undefined;
    }
  };

  const [{ isDragging }, dragRef] = useDrag({
    item: { id: uuidv4(), subType: id, props: defaultPropsByType(id, getRandomValue(id)) },
    type: id === TYPES.QUESTION ? TYPES.QUESTION : type,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  return (
    <InfoTooltip
      content={isWebhookDisabled ? t('dialog.toolkit_disabled') : ''}
      open={isWebhookDisabled ? undefined : false}
      side="top"
      showArrow
    >
      <div
        className={cn(styles.item, {
          [styles.isDragging]: isDragging,
          [styles.isWebhookDisabled]: isWebhookDisabled,
          [styles.isDisabled]: !canWrite,
        })}
        ref={(node) => {
          if (canWrite && !isWebhookDisabled) {
            dragRef(node);
          }
        }}
      >
        {!isDragging && (
          <>
            <div className={cn(styles.icon, { [styles.isDragging]: isDragging })}>
              {iconByType({ id, color: 'var(--color-foreground-muted)', size: 24, type })}
            </div>
            <span
              className={cn('text-sm leading-5 font-normal text-foreground', styles.text, {
                [styles.isDragging]: isDragging,
              })}
            >
              {t(title)}
            </span>
          </>
        )}
      </div>
    </InfoTooltip>
  );
};

export default ToolkitItem;
